import type { GameContext } from '../core/context';
import type { BodyTagData, DamageSide } from '../core/events';
import { clamp, clamp01, distanceSq, type V2 } from '../core/math';
import type { Rng } from '../core/rng';
import type { System } from '../core/system';
import { Layer } from '../physics/types';
import { PUNCTURE_GRIP, PUNCTURE_MAX, PUNCTURE_TOP } from '../police/tuning';
import { CHAIN_IGNITE_HEALTH, CHAIN_IGNITE_RADIUS, POLICE_FIRE_FUSE_S } from '../../shared/limits';
import { damageModifiers, stepVehicleModel } from './model';
import { getSpec } from './specs';
import { Frustum, Matrix4, Sphere, Vector3 } from 'three';
import { LOCKUP_CAR_TAG, MOTOR_TAG, NET_CAR_TAG, NET_REMOTE_TAG, type SpawnOptions, type Vehicle, type VehicleSpec } from './types';
import { VehicleEntity } from './vehicle';

/** Public API of the vehicles module (`ctx.services.get('vehicles')`). */
export interface VehicleService {
  spawn(specId: string, pos: V2, opts?: SpawnOptions): Vehicle;
  despawn(v: Vehicle | number): void;
  get(id: number): Vehicle | undefined;
  byBodyId(bodyId: number): Vehicle | undefined;
  all(): readonly Vehicle[];
  /** Closest vehicle within radius matching filter. */
  nearest(pos: V2, radius: number, filter?: (v: Vehicle) => boolean): Vehicle | null;
  /** All vehicles within radius. */
  query(pos: V2, radius: number, out?: Vehicle[]): Vehicle[];
  /** Apply damage from a non-collision source (explosions, gunfire, scripted). */
  applyDamage(v: Vehicle, amount: number, side: DamageSide, byPlayer: boolean, localPoint?: V2): void;
  /** A tyre shot out or spiked on that side (at most PUNCTURE_MAX in all); emits `vehiclePunctured`. */
  puncture(v: Vehicle, side: 'left' | 'right', cause: 'shot' | 'stinger'): void;
  repair(v: Vehicle): void;
  /** Immediately set a vehicle on fire (scripted moments). */
  ignite(v: Vehicle, seconds?: number): void;
}

declare module '../core/services' {
  interface ServiceMap {
    vehicles: VehicleService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Damage a vehicle (side: front|rear|left|right). */
    damageVehicle(id: number, amount: number, side?: string): void;
    repairVehicle(id: number): void;
  }
}

/**
 * A small engine tune on one car (Halloran's workshop, src/activities): multipliers on the model's power
 * and top speed, set on `vehicle.data.tune`. Applied offline only. Spec numbers are never changed.
 */
export interface VehicleTune {
  power: number;
  topSpeed: number;
}

/** Health lost per m/s of impact delta-v above the threshold (before spec.fragility). */
export const DAMAGE_PER_MS = 3.2;
/** Delta-v (m/s) below which impacts do no damage — parking taps and kerb scrapes are free. */
export const DAMAGE_THRESHOLD = 4;
/** One impact can take at most this fraction of max health unless it is truly violent (playtest DRIVING-1). */
export const SINGLE_HIT_CAP = 0.45;
/** Delta-v (m/s) above which the single-hit cap no longer applies (head-on at 40+ mph). */
export const VIOLENT_DV = 18;
/** Seconds a vehicle burns at 0 health before exploding. */
export const FIRE_FUSE = 4.5;
export const EXPLOSION_RADIUS = 9;
/**
 * Abandoned-car culling (playtest PERF-2): a car nobody manages any more (no occupant, none of the
 * module tags below) that has stayed this far from the player for ABANDON_CULL_SECONDS is despawned,
 * so stolen/wrecked/dumped cars don't accumulate forever. A car the player owns without a kept tag
 * (a resprayed car, the crew van / Regent after a job or a replay, a minicab) goes the same way, but
 * only while it is also out of view, and never the car the player last drove. The kept car (the
 * Lockup 7 car, the garage's "Keep this one" motor) is never culled.
 */
export const ABANDON_CULL_DISTANCE = 170;
export const ABANDON_CULL_SECONDS = 20;
const MANAGED_TAGS = ['mission', 'parked', 'traffic', 'police', 'police-unit', 'roadblock', NET_REMOTE_TAG, NET_CAR_TAG, LOCKUP_CAR_TAG, MOTOR_TAG];

/**
 * Health lost from one collision impulse (N·s). Impulse includes the rebound, so restitution is
 * divided back out to get the impact speed; one non-violent hit can't take more than SINGLE_HIT_CAP.
 */
export function impactDamage(spec: VehicleSpec, impulse: number): number {
  const dv = impulse / spec.mass / (1 + spec.restitution);
  const amount = (dv - DAMAGE_THRESHOLD) * DAMAGE_PER_MS * spec.fragility;
  if (amount <= 0) return 0;
  return dv < VIOLENT_DV ? Math.min(amount, spec.maxHealth * SINGLE_HIT_CAP) : amount;
}

export function createVehicleSystem(): System {
  let ctx: GameContext;
  let rng: Rng;
  const vehicles: VehicleEntity[] = [];
  const byId = new Map<number, VehicleEntity>();
  const byBody = new Map<number, VehicleEntity>();
  let nextId = 1;
  const offs: (() => void)[] = [];
  let cullClock = 0;
  const farFor = new Map<number, number>();
  /** The car the player was in last (never culled, even when owned, untagged and far). */
  let lastDriven = -1;
  const frustum = new Frustum();
  const viewMatrix = new Matrix4();
  const sphere = new Sphere(new Vector3(), 4);

  /** Headless (no renderer): nothing is on screen. */
  function inView(v: Vehicle): boolean {
    if (!ctx.render) return false;
    const cam = ctx.camera;
    frustum.setFromProjectionMatrix(viewMatrix.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse));
    sphere.center.set(v.pos.x, 1, v.pos.z);
    return frustum.intersectsSphere(sphere);
  }

  function spawn(specId: string, pos: V2, opts: SpawnOptions = {}): Vehicle {
    const spec = getSpec(specId);
    const isPolice = spec.class === 'police';
    const id = nextId++;
    const body = ctx.physics.createBody({
      shape: { type: 'box', hx: spec.width / 2, hz: spec.length / 2 },
      pos,
      angle: opts.heading ?? 0,
      mass: spec.mass,
      inertiaScale: spec.inertiaScale,
      restitution: spec.restitution,
      friction: 0.3,
      angularDamping: 0.6,
      layer: Layer.VEHICLE,
      mask: Layer.ALL,
      kind: 'vehicle',
      entityId: id,
    });
    const paint = opts.paint ?? rng.pick(spec.paints);
    const v = new VehicleEntity(id, spec, body, isPolice, paint);
    v.controller = opts.controller ?? 'none';
    v.ownedByPlayer = opts.ownedByPlayer ?? false;
    v.locked = opts.locked ?? false;
    v.occupant = opts.occupant ?? null;
    v.telemetry.topSpeed = spec.maxSpeed;
    v.telemetry.rpm = spec.idleRpm;
    for (const t of opts.tags ?? []) v.tags.add(t);
    if (opts.parked) {
      body.sleeping = true;
      v.controls.handbrake = true;
      v.headlights = false;
    }
    vehicles.push(v);
    byId.set(id, v);
    byBody.set(body.id, v);
    return v;
  }

  function despawn(target: Vehicle | number): void {
    const v = typeof target === 'number' ? byId.get(target) : byId.get(target.id);
    if (!v) return;
    ctx.physics.removeBody(v.body);
    byId.delete(v.id);
    byBody.delete(v.body.id);
    const i = vehicles.indexOf(v);
    if (i >= 0) vehicles.splice(i, 1);
  }

  function nearest(pos: V2, radius: number, filter?: (v: Vehicle) => boolean): Vehicle | null {
    let best: Vehicle | null = null;
    let bestD = radius * radius;
    for (const v of vehicles) {
      const d = distanceSq(pos, v.pos);
      if (d <= bestD && (!filter || filter(v))) {
        best = v;
        bestD = d;
      }
    }
    return best;
  }

  function query(pos: V2, radius: number, out: Vehicle[] = []): Vehicle[] {
    const r2 = radius * radius;
    for (const v of vehicles) if (distanceSq(pos, v.pos) <= r2) out.push(v);
    return out;
  }

  function sideOfLocal(v: VehicleEntity, l: V2): DamageSide {
    const nx = Math.abs(l.x) / (v.spec.width / 2);
    const nz = Math.abs(l.z) / (v.spec.length / 2);
    if (nz >= nx * 0.85) return l.z >= 0 ? 'front' : 'rear';
    // Local +X is the vehicle's LEFT side.
    return l.x >= 0 ? 'left' : 'right';
  }

  function updateCondition(v: VehicleEntity): void {
    if (v.condition === 'wrecked' || v.condition === 'burning') return;
    const h = v.health / v.spec.maxHealth;
    v.condition = h <= 0 ? 'burning' : h < 0.2 ? 'heavySmoke' : h < 0.4 ? 'smoking' : 'ok';
    if (v.condition === 'burning') ignite(v);
  }

  function applyDamage(target: Vehicle, amount: number, side: DamageSide, byPlayer: boolean, localPoint?: V2): void {
    const v = byId.get(target.id);
    if (!v || amount <= 0 || v.condition === 'wrecked') return;
    // Online: another player's car takes its damage on the server; we only draw what it reports.
    if (v.tags.has(NET_REMOTE_TAG)) return;
    if (byPlayer) v.lastPlayerHitTime = ctx.state.time;
    const prevFront = v.damage.front;
    const prevRear = v.damage.rear;
    v.damage[side] = clamp01(v.damage[side] + (amount / v.spec.maxHealth) * 1.6);
    if (localPoint) {
      v.dents.push({ x: localPoint.x, z: localPoint.z, depth: clamp(amount / 40, 0.05, 0.45) });
      if (v.dents.length > 24) v.dents.shift();
    }
    v.damageVersion++;
    if (!v.destroyed) {
      v.health = Math.max(0, v.health - amount);
      ctx.events.emit('vehicleDamaged', { vehicleId: v.id, amount, side, health: v.health });
      updateCondition(v);
    }
    // Lights breaking scatter glass.
    const particles = ctx.services.tryGet('particles');
    if (particles) {
      const broke = (prevFront < 0.35 && v.damage.front >= 0.35) || (prevRear < 0.35 && v.damage.rear >= 0.35);
      const lz = side === 'front' ? v.spec.length / 2 : side === 'rear' ? -v.spec.length / 2 : 0;
      const p = v.body.localToWorld(localPoint ?? { x: 0, z: lz });
      if (broke) particles.emit('glass', { x: p.x, y: 0.7, z: p.z }, { count: 18 });
      if (amount > 12) particles.emit('debris', { x: p.x, y: 0.6, z: p.z }, { count: Math.min(14, Math.round(amount / 5)), color: v.paint });
    }
  }

  function ignite(target: Vehicle, seconds?: number): void {
    const v = byId.get(target.id);
    if (!v || v.condition === 'wrecked' || v.tags.has(NET_REMOTE_TAG)) return;
    // A police car burns longer before it goes up (docs/POLICE-AI-POLISH.md).
    seconds ??= v.isPolice ? POLICE_FIRE_FUSE_S : FIRE_FUSE;
    const wasDestroyed = v.destroyed;
    v.health = 0;
    v.destroyed = true;
    v.condition = 'burning';
    v.fireTimer = seconds;
    v.siren = false;
    v.headlights = false;
    if (!wasDestroyed) {
      const byPlayer = v.controller === 'player' || ctx.state.time - v.lastPlayerHitTime < 4;
      ctx.events.emit('vehicleDestroyed', { vehicleId: v.id, pos: { x: v.pos.x, z: v.pos.z }, byPlayer });
    }
  }

  function explode(v: VehicleEntity): void {
    v.condition = 'wrecked';
    v.damageVersion++;
    const pos = { x: v.pos.x, z: v.pos.z };
    const byPlayer = ctx.state.time - v.lastPlayerHitTime < 8 || v.controller === 'player';
    // Self: hop and spin.
    v.body.applyAngularImpulse((rng.chance(0.5) ? 1 : -1) * v.body.inertia * 1.8);
    // Area impulse + damage (chain reactions are intended).
    const hits = ctx.physics.queryCircle(pos, EXPLOSION_RADIUS, Layer.ALL & ~Layer.STATIC);
    for (const b of hits) {
      if (b === v.body) continue;
      const dx = b.pos.x - pos.x;
      const dz = b.pos.z - pos.z;
      const d = Math.hypot(dx, dz) || 0.01;
      const fall = clamp01(1 - d / EXPLOSION_RADIUS);
      const j = Math.min(14000 * fall, b.mass * 14);
      b.applyImpulse({ x: (dx / d) * j, z: (dz / d) * j });
      b.applyAngularImpulse((rng.range(-1, 1) * fall * b.inertia * 2) | 0);
      const other = byBody.get(b.id);
      if (other && other !== v) {
        const l = other.body.worldToLocal(pos);
        applyDamage(other, 95 * fall, sideOfLocal(other, l), byPlayer, l);
        // Chain reaction (docs/POLICE-AI-POLISH.md): a badly hurt car right next to the blast catches fire too,
        // with its own fuse, so a roadblock goes up one car after another.
        if (!other.destroyed && d < CHAIN_IGNITE_RADIUS && other.health < other.spec.maxHealth * CHAIN_IGNITE_HEALTH && !other.tags.has(NET_REMOTE_TAG)) {
          if (byPlayer) other.lastPlayerHitTime = ctx.state.time;
          ignite(other, (other.isPolice ? POLICE_FIRE_FUSE_S : FIRE_FUSE) * (0.6 + rng.next() * 0.5));
        }
      }
    }
    // The look and sound of it (fireball, ring, debris, flash, peds knocked flat) belong to fx/explosionFx.ts and audio.
    ctx.events.emit('vehicleExploded', { vehicleId: v.id, pos, byPlayer });
  }

  function repair(target: Vehicle): void {
    const v = byId.get(target.id);
    if (!v) return;
    v.health = v.spec.maxHealth;
    v.damage = { front: 0, rear: 0, left: 0, right: 0 };
    v.punctures = { left: 0, right: 0 };
    v.destroyed = false;
    v.condition = 'ok';
    v.fireTimer = 0;
    v.dents.length = 0;
    v.damageVersion++;
  }

  function puncture(target: Vehicle, side: 'left' | 'right', cause: 'shot' | 'stinger'): void {
    const v = byId.get(target.id);
    if (!v || v.destroyed) return;
    if (v.punctures.left + v.punctures.right >= PUNCTURE_MAX) return;
    v.punctures[side]++;
    if (v.body.sleeping) v.body.wake();
    ctx.events.emit('vehiclePunctured', { vehicleId: v.id, side, cause, flats: v.punctures.left + v.punctures.right, pos: { x: v.pos.x, z: v.pos.z } });
  }

  const service: VehicleService = {
    spawn,
    despawn,
    puncture,
    get: (id) => byId.get(id),
    byBodyId: (id) => byBody.get(id),
    all: () => vehicles,
    nearest,
    query,
    applyDamage,
    repair,
    ignite,
  };

  function isPlayerTag(t: BodyTagData | null): boolean {
    if (!t) return false;
    if (t.kind === 'player') return true;
    return t.kind === 'vehicle' && byId.get(t.id)?.controller === 'player';
  }

  return {
    id: 'vehicles',
    order: 300,
    init(c) {
      ctx = c;
      rng = c.rng.fork('vehicles');
      c.services.provide('vehicles', service);

      offs.push(
        c.events.on('vehicleEntered', (e) => {
          if (e.byPlayer) lastDriven = e.vehicleId;
        }),
        c.events.on('collision', (e) => {
          const va = e.a.kind === 'vehicle' ? byId.get(e.a.id) : undefined;
          const vb = e.b && e.b.kind === 'vehicle' ? byId.get(e.b.id) : undefined;
          if (!va && !vb) return;
          const playerInvolved = isPlayerTag(e.a) || isPlayerTag(e.b);
          for (const v of [va, vb]) {
            if (!v) continue;
            // Soft things (peds, light props) don't dent cars.
            const other = v === va ? e.b : e.a;
            if (other && (other.kind === 'ped' || other.kind === 'player' || other.kind === 'prop')) continue;
            // Online, a crash with another player's networked car is judged by the server (`vhit`).
            if (other && other.kind === 'vehicle' && byId.get(other.id)?.tags.has(NET_REMOTE_TAG)) continue;
            const amount = impactDamage(v.spec, e.impulse);
            if (amount <= 0) continue;
            const l = v.body.worldToLocal(e.pos);
            applyDamage(v, amount, sideOfLocal(v, l), playerInvolved && v.controller !== 'player', l);
          }
        }),
      );

      c.hooks.register('spawnVehicle', (type, x, z, heading = 0) => spawn(type, { x, z }, { heading }).id);
      c.hooks.register('damageVehicle', (id, amount, side = 'front') => {
        const v = byId.get(id);
        if (v) applyDamage(v, amount, side as DamageSide, false, { x: 0, z: side === 'rear' ? -v.spec.length / 2 : v.spec.length / 2 });
      });
      c.hooks.register('repairVehicle', (id) => {
        const v = byId.get(id);
        if (v) repair(v);
      });
      c.hooks.registerSnapshot('vehicles', () => ({
        count: vehicles.length,
        awake: vehicles.filter((v) => !v.body.sleeping).length,
        list: vehicles.slice(0, 64).map((v) => ({
          id: v.id,
          spec: v.spec.id,
          x: +v.pos.x.toFixed(2),
          z: +v.pos.z.toFixed(2),
          heading: +v.heading.toFixed(3),
          speed: +v.speed.toFixed(2),
          health: +v.health.toFixed(1),
          condition: v.condition,
          damage: { f: +v.damage.front.toFixed(2), r: +v.damage.rear.toFixed(2), l: +v.damage.left.toFixed(2), rt: +v.damage.right.toFixed(2) },
          topSpeed: +v.telemetry.topSpeed.toFixed(1),
          skid: +v.telemetry.skid.toFixed(2),
          controller: v.controller,
          occupant: v.occupant?.kind ?? null,
          police: v.isPolice,
          locked: v.locked,
          destroyed: v.destroyed,
        })),
      }));
    },
    fixedUpdate(dt) {
      // Abandoned-car culling, checked once a second.
      cullClock += dt;
      if (cullClock >= 1) {
        const step = cullClock;
        cullClock = 0;
        const player = ctx.services.tryGet('player');
        const pp = player?.pos;
        if (player?.vehicle) lastDriven = player.vehicle.id;
        if (pp) {
          for (const v of vehicles.slice()) {
            const managed = v.occupant !== null || v.id === lastDriven || MANAGED_TAGS.some((t) => v.tags.has(t));
            const far = (v.pos.x - pp.x) ** 2 + (v.pos.z - pp.z) ** 2 > ABANDON_CULL_DISTANCE ** 2;
            if (managed || !far || v.condition === 'burning' || (v.ownedByPlayer && inView(v))) {
              farFor.delete(v.id);
              continue;
            }
            const t = (farFor.get(v.id) ?? 0) + step;
            if (t >= ABANDON_CULL_SECONDS) {
              farFor.delete(v.id);
              despawn(v);
            } else farFor.set(v.id, t);
          }
        }
      }
      // Fire fuse -> explosion.
      for (const v of vehicles) {
        if (v.condition !== 'burning') continue;
        v.fireTimer -= dt;
        if (v.fireTimer <= 0) explode(v);
      }
      // Halloran's performance tune (src/activities, `vehicle.data.tune`): offline only — online the
      // server's handling numbers are the only ones (it validates every predicted pose).
      const tuneOk = !ctx.services.tryGet('net')?.online;
      for (const v of vehicles) {
        const body = v.body;
        if (!body.enabled) continue;
        if (v.occupant === null || v.destroyed) {
          // Nobody driving: handbrake on, wheels straight.
          v.controls.throttle = 0;
          v.controls.steer = 0;
          v.controls.handbrake = true;
        }
        // Networked remote cars are moved by the net module (another client simulated them already).
        if (body.sleeping || v.tags.has(NET_REMOTE_TAG)) continue;
        const mods = damageModifiers(v.spec, v.health, v.damage, v.destroyed);
        const flats = v.punctures.left + v.punctures.right;
        if (flats > 0) {
          // Shot-out tyres (docs/POLICE-AI-POLISH.md): less grip and pace, a pull towards the flat side.
          mods.grip *= Math.max(0.4, 1 - PUNCTURE_GRIP * flats);
          mods.topSpeed *= Math.max(0.6, 1 - PUNCTURE_TOP * flats);
          mods.steerPull = clamp(mods.steerPull + (v.punctures.right - v.punctures.left) * 0.05, -0.2, 0.2);
        }
        // The arcade drift layer is the player's (docs/DRIVING.md §1): AI drivers keep the plain grip
        // model their lines are tuned for.
        if (v.controller === 'player') mods.drift = 1;
        const tune = tuneOk ? (v.data.tune as VehicleTune | undefined) : undefined;
        if (tune) {
          mods.power *= tune.power;
          mods.topSpeed *= tune.topSpeed;
        }
        stepVehicleModel(body, v.spec, v.controls, v.telemetry, mods, dt);
      }
    },
    dispose() {
      for (const off of offs) off();
      for (const v of vehicles.slice()) despawn(v);
    },
  };
}

/**
 * Post-physics (order 450): scrape/impact telemetry, sparks, fire fuse and explosions.
 * Reads `physics.contacts` directly (all touching pairs, including continuous scrapes).
 */
export function createVehiclePostSystem(): System {
  let ctx: GameContext;
  let vehicles: VehicleService;

  return {
    id: 'vehicles-post',
    order: 450,
    init(c) {
      ctx = c;
    },
    start() {
      vehicles = ctx.services.get('vehicles');
    },
    fixedUpdate() {
      const all = vehicles.all();
      for (const v of all) {
        v.telemetry.scrape = 0;
        v.telemetry.impact = 0;
      }
      const particles = ctx.services.tryGet('particles');
      for (const c of ctx.physics.contacts) {
        const va = c.a.kind === 'vehicle' ? vehicles.get(c.a.id) : undefined;
        const vb = c.b && c.b.kind === 'vehicle' ? vehicles.get(c.b.id) : undefined;
        if (!va && !vb) continue;
        const soft = c.b && (c.b.kind === 'ped' || c.b.kind === 'player' || c.b.kind === 'prop');
        if (soft || c.a.kind === 'ped' || c.a.kind === 'player') continue;
        const scrape = c.slide > 2 ? clamp01((c.slide - 2) / 14) : 0;
        const impact = c.isNew ? c.speed : 0;
        for (const v of [va, vb]) {
          if (!v) continue;
          v.telemetry.scrape = Math.max(v.telemetry.scrape, scrape);
          v.telemetry.impact = Math.max(v.telemetry.impact, impact);
        }
        if (!particles) continue;
        const p3 = { x: c.pos.x, y: 0.45, z: c.pos.z };
        const mover = va ?? vb!;
        if (scrape > 0 && c.impulse > 20) {
          // Sparks fly opposite to the sliding direction of the mover.
          const vel = mover.velocity;
          const sp = Math.hypot(vel.x, vel.z) || 1;
          particles.emit('sparks', p3, { count: 1 + Math.round(scrape * 3), dir: { x: -vel.x / sp, z: -vel.z / sp }, intensity: 0.6 + scrape });
        }
        if (c.isNew && c.speed > 5) {
          particles.emit('sparks', p3, { count: Math.min(24, Math.round(c.speed)), intensity: clamp01(c.speed / 15) + 0.5 });
          particles.emit('dust', p3, { count: 3 });
        }
      }
    },
  };
}
